<template>
  <Main>
    <v-row>
      <v-col cols="12" sm="8" md="9" class="pr-sm-5">
        <BaseFilter
          v-model="search"
          v-bind="filterProps"
          textPlaceholder="Buscar"
          @search="searchByFilter"
          v-on:keyup="searchByFilterClickEnter"
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" class="mb-5" v-if="canCreateReason">
        <BaseButton
          class="secondary text-uppercase subtitle-2 font-weight-bold"
          width="100%"
          @click="newReason()"
        >
          <v-icon class="pr-4 add-icon">fa-plus</v-icon>
          Novo Motivo
        </BaseButton>
      </v-col>
      <v-col cols="12">
        <BaseDataTable
          :headers="tableHeaders"
          :items="reasons.items"
          :server-items-length="reasons.total_count"
          no-data-text="Não há motivos cadastrados."
          :options.sync="filterOptions"
          :loading="listLoading"
          class="reason_table base__datatable"
          @click:row="editReason"
        >
          <template v-slot:[`item.descricao`]="{ item }">
            <div :class="borderColor(item.status.id)">
              <div class="reason_table__padding">
                {{ item.descricao }}
              </div>
            </div>
          </template>
          <template v-slot:[`item.modalidades`]="{ item }">
            {{ concatNamesModalities(item) }}
          </template>
          <template v-slot:[`item.ativo`]="{ item }">
            {{ item.status.nome }}
          </template>
        </BaseDataTable>
      </v-col>
    </v-row>
    <ModalReason
      v-if="modal"
      v-model="modal"
      @close="closeModal"
      @submit="submit"
      :form="form"
      :is-edit="isEdit"
      :modalities="modalities"
      :title="title"
      :button-name="buttonName"
    />
  </Main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import { defaultFilterOptions } from "@/mixins/PayloadDataTableMixin";
import { CommonPayload } from "@/mixins/PayloadDataTableMixin";
import ModalReason from "../components/ModalReason";
const defaultFilterProps = {
  outlined: true,
  dense: true,
  "background-color": "white",
  class: "filter__icon",
  "append-outer-icon": "fa-search"
};

const tableHeaders = [
  { text: "Descrição", value: "descricao", sortable: true },
  { text: "Modalidades", value: "modalidades", sortable: false },
  { text: "Status", value: "ativo", sortable: true }
];

const colorByStatus = {
  ativo: { color: "light-blue" },
  inativo: { color: "pink" }
};

export default {
  name: "ReasonList",
  mixins: [LoadingRequest, CommonPayload],
  components: {
    ModalReason
  },
  computed: {
    ...mapState("reason_cancel", ["reasons", "modalities"]),
    ...mapState(["userPermissions"]),
    canCreateReason() {
      return this.userPermissions.acoes.some(
        x => x.id == "criar_motivo_cancelamento"
      );
    },
    canEditReason() {
      return this.userPermissions.acoes.some(
        x => x.id == "editar_motivo_cancelamento"
      );
    }
  },
  data: () => ({
    search: "",
    filterProps: defaultFilterProps,
    filterOptions: defaultFilterOptions(),
    tableHeaders,
    listLoading: false,
    modal: false,
    form: {},
    isEdit: false,
    title: null,
    buttonName: null
  }),
  watch: {
    filterOptions: {
      handler(value) {
        this.fetchData(this.getPayload(value));
      },
      deep: true
    }
  },
  mounted() {
    this.fetchData(this.getPayload(this.filterOptions));
    this.withLoading(this.requestModalities());
  },
  methods: {
    ...mapActions("reason_cancel", [
      "requestReasonList",
      "requestModalities",
      "requestReason",
      "requestCreateReason",
      "requestEditReason"
    ]),
    ...mapActions(["showAlertNotification"]),
    borderColor(status) {
      const { color } = colorByStatus[status];
      return `reason_table__border-${color}`;
    },
    searchByFilter() {
      this.withLoading(
        this.requestReasonList(this.getPayload(this.filterOptions))
      );
    },
    searchByFilterClickEnter(event) {
      if (event.keyCode === 13)
        this.withLoading(
          this.requestReasonList(this.getPayload(this.filterOptions))
        );
    },
    fetchData(payload) {
      this.withLoading(this.requestReasonList(payload));
    },
    editReason(item) {
      if (this.canEditReason)
        this.withLoading(
          this.requestReason(item.id).then(data => {
            this.form = data;
            this.isEdit = true;
            this.title = "EDITAR MOTIVO";
            this.buttonName = "SALVAR";
            this.modal = true;
          })
        );
    },
    newReason() {
      this.form = {
        ativo: true
      };
      this.isEdit = false;
      this.title = "NOVO MOTIVO";
      this.buttonName = "CRIAR";
      this.modal = true;
    },
    getPayload(options) {
      const { page, itemsPerPage, sortDesc, sortBy } = options;
      const direction =
        sortBy.length == 1 ? (sortDesc[0] ? "desc" : "asc") : "desc";
      const sort = sortBy.length == 1 ? sortBy[0] : "";
      return {
        page,
        page_size: itemsPerPage,
        sort,
        direction,
        query: this.search,
        somente_ativos: false
      };
    },
    closeModal() {
      this.modal = false;
    },
    concatNamesModalities(item) {
      return item.modalidades
        .map(item => item.nome)
        .flat()
        .join(", ");
    },
    submit(form) {
      if (this.isEdit) {
        this.withLoading(
          this.requestEditReason({ id: form.id, payload: form }).then(() => {
            this.showAlertNotification({
              message: "Motivo editado com sucesso",
              type: "success"
            });
            this.modal = false;
            this.searchByFilter();
          })
        );
      } else {
        this.withLoading(
          this.requestCreateReason(form).then(() => {
            this.showAlertNotification({
              message: "Motivo criado com sucesso",
              type: "success"
            });
            this.modal = false;
            this.searchByFilter();
          })
        );
      }
    }
  }
};
</script>

<style lang="sass">
.reason_table
  &__border-light-blue
    border-left: 5px solid #49DCBB
  &__border-pink
    border-left: 5px solid #EF5350
  &__padding
    padding: 0px 15px !important
    height: 36px !important
    display: flex
    align-items: center
  &.v-data-table.theme--light
    thead tr th
      padding: 8px 8px
    tbody tr td
      padding: 18px 8px
    tbody tr td:first-child
      padding: 2px 0px !important
</style>
